import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { eventsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import {
  Calendar,
  ArrowLeft,
  Clock,
  MapPin,
  Users,
  Check,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

export default function EventsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('list'); // 'list' | 'calendar'

  const hoaId = user?.hoa_id || 'demo_hoa';
  
  // Determine back navigation based on user role
  const getBackPath = () => {
    if (user?.role === 'trustee' || user?.role === 'property_manager') return '/admin';
    if (user?.role === 'managing_agent') return '/agent';
    if (user?.role === 'platform_owner') return '/platform';
    return '/dashboard';
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const data = await eventsApi.list(hoaId).catch(() => [
        { event_id: '1', title: 'Annual General Meeting', event_type: 'agm', start_time: new Date(Date.now() + 86400000 * 3).toISOString(), end_time: new Date(Date.now() + 86400000 * 3 + 7200000).toISOString(), location: 'Clubhouse', description: 'Yearly AGM to discuss budget, elections, and community matters.', attendees: ['user1', 'user2', 'user3'] },
        { event_id: '2', title: 'Community BBQ', event_type: 'community', start_time: new Date(Date.now() + 86400000 * 7).toISOString(), end_time: new Date(Date.now() + 86400000 * 7 + 14400000).toISOString(), location: 'Pool Area', description: 'Join us for our monthly community BBQ! Bring your favorite dish to share.', attendees: ['user1'] },
        { event_id: '3', title: 'Fire Drill', event_type: 'security_drill', start_time: new Date(Date.now() + 86400000 * 14).toISOString(), end_time: new Date(Date.now() + 86400000 * 14 + 3600000).toISOString(), location: 'Main Building', description: 'Mandatory fire safety drill for all residents.', attendees: [] },
        { event_id: '4', title: 'Garden Maintenance', event_type: 'maintenance', start_time: new Date(Date.now() + 86400000 * 5).toISOString(), end_time: new Date(Date.now() + 86400000 * 5 + 28800000).toISOString(), location: 'Common Areas', description: 'Scheduled garden and landscaping maintenance.', attendees: [] },
      ]);
      setEvents(data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async (eventId) => {
    try {
      await eventsApi.rsvp(eventId);
      toast.success('RSVP confirmed!');
      // Update local state
      setEvents(events.map(e => 
        e.event_id === eventId 
          ? { ...e, attendees: [...(e.attendees || []), user?.user_id] }
          : e
      ));
    } catch (error) {
      toast.error('Failed to RSVP');
    }
  };

  const getEventTypeColor = (type) => {
    const colors = {
      agm: 'bg-purple-100 text-purple-700',
      community: 'bg-emerald-100 text-emerald-700',
      maintenance: 'bg-amber-100 text-amber-700',
      security_drill: 'bg-red-100 text-red-700',
      general: 'bg-sky-100 text-sky-700',
    };
    return colors[type] || colors.general;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const isAttending = (event) => {
    return event.attendees?.includes(user?.user_id);
  };

  // Get dates with events for calendar highlighting
  const eventDates = events.map(e => new Date(e.start_time));

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(getBackPath())} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">Events</h1>
          </div>
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            <Button
              variant={view === 'list' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-md ${view === 'list' ? 'bg-white shadow-sm' : ''}`}
              onClick={() => setView('list')}
              data-testid="view-list"
            >
              List
            </Button>
            <Button
              variant={view === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-md ${view === 'calendar' ? 'bg-white shadow-sm' : ''}`}
              onClick={() => setView('calendar')}
              data-testid="view-calendar"
            >
              Calendar
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-4">
        {view === 'calendar' && (
          <Card className="mb-4">
            <CardContent className="p-3">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                modifiers={{ hasEvent: eventDates }}
                modifiersStyles={{ hasEvent: { fontWeight: 'bold', textDecoration: 'underline', color: '#0EA5E9' } }}
                className="rounded-md"
              />
            </CardContent>
          </Card>
        )}

        {/* Events List */}
        <div className="space-y-3">
          {loading ? (
            <>
              <Skeleton className="h-32 rounded-xl" />
              <Skeleton className="h-32 rounded-xl" />
              <Skeleton className="h-32 rounded-xl" />
            </>
          ) : events.length > 0 ? (
            events.map((event, index) => (
              <Card 
                key={event.event_id} 
                className="overflow-hidden animate-slideUp"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`event-${event.event_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-sky-50 text-sky-600 flex-shrink-0">
                      <span className="text-xs font-medium uppercase">
                        {new Date(event.start_time).toLocaleDateString('en-US', { month: 'short' })}
                      </span>
                      <span className="text-2xl font-bold">
                        {new Date(event.start_time).getDate()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <Badge variant="secondary" className={`text-xs mb-1 ${getEventTypeColor(event.event_type)}`}>
                        {event.event_type.replace('_', ' ')}
                      </Badge>
                      <p className="font-medium text-slate-900">{event.title}</p>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-500">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{formatTime(event.start_time)}</span>
                        </div>
                        {event.location && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            <span>{event.location}</span>
                          </div>
                        )}
                        {event.attendees?.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" />
                            <span>{event.attendees.length} attending</span>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 line-clamp-2 mt-2">{event.description}</p>
                      <div className="mt-3">
                        {isAttending(event) ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <Check className="w-3 h-3 mr-1" />
                            Attending
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full"
                            onClick={() => handleRSVP(event.event_id)}
                            data-testid={`rsvp-${event.event_id}`}
                          >
                            RSVP
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">No upcoming events</h3>
                <p className="text-slate-500 text-sm">Check back later for community events</p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
